import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCalendarConnections,
  openConnectModal,
  syncConnection,
  disconnectConnection,
  openManage,
} from '../ducks';
import {
  selectConnections,
  selectConnectionsLoading,
  selectConnectionsError,
  selectModalStep,
} from '../selectors';
import { AddConnectionModal } from './AddConnectionModal';

function formatRelative(iso?: string): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'never';
  const delta = (Date.now() - t) / 1000;
  if (delta < 60) return 'just now';
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
}

export function CalendarConnectionsSection(): JSX.Element {
  const dispatch = useDispatch();
  const connections = useSelector(selectConnections);
  const loading = useSelector(selectConnectionsLoading);
  const error = useSelector(selectConnectionsError);
  const modalStep = useSelector(selectModalStep);

  useEffect(() => {
    dispatch(fetchCalendarConnections());
  }, [dispatch]);

  return (
    <section className="settings-section">
      <h2 className="settings-section__title">Calendar imports</h2>
      <p className="settings-section__hint">
        Sync events from your Apple iCloud or Google calendars into your household calendar.
      </p>

      {error && (
        <div className="settings-section__error" role="alert">
          {error}
        </div>
      )}

      {loading && connections.length === 0 ? (
        <div role="status">Loading…</div>
      ) : connections.length === 0 ? (
        <div className="calendar-connections__empty">
          No calendars connected yet.
        </div>
      ) : (
        <ul className="calendar-connections__list">
          {connections.map((conn) => {
            const selectedCount =
              conn.provider === 'google'
                ? conn.selected_google_calendar_ids?.length ?? 0
                : conn.selected_calendar_uris?.length ?? 0;
            const lastSyncedISO = conn.last_synced_at
              ? new Date(conn.last_synced_at as unknown as string).toISOString()
              : undefined;
            return (
              <li key={conn.id} className="calendar-connections__row">
                <div className="calendar-connections__row-main">
                  <span className={`calendar-connections__badge calendar-connections__badge--${conn.provider}`}>
                    {conn.provider === 'apple' ? 'Apple' : 'Google'}
                  </span>
                  <span className="calendar-connections__email">{conn.email || '(no email)'}</span>
                  <span className="calendar-connections__meta">
                    {selectedCount > 0
                      ? `${selectedCount} calendar${selectedCount === 1 ? '' : 's'}`
                      : conn.provider === 'google' ? 'primary only' : 'all calendars'}
                    {' · last synced '}{formatRelative(lastSyncedISO)}
                  </span>
                </div>
                <div className="calendar-connections__row-actions">
                  <button
                    type="button"
                    className="settings-section__btn"
                    onClick={() => dispatch(openManage(conn.id))}
                  >
                    Manage
                  </button>
                  <button
                    type="button"
                    className="settings-section__btn"
                    onClick={() => dispatch(syncConnection(conn.id, conn.provider))}
                  >
                    Sync now
                  </button>
                  <button
                    type="button"
                    className="settings-section__btn settings-section__btn--danger"
                    onClick={() => {
                      const ok = window.confirm(`Disconnect this ${conn.provider} calendar?`);
                      if (ok) dispatch(disconnectConnection(conn.id, conn.provider));
                    }}
                  >
                    Disconnect
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        className="settings-section__btn settings-section__btn--primary"
        onClick={() => dispatch(openConnectModal())}
      >
        Connect calendar
      </button>

      {modalStep !== 'closed' && <AddConnectionModal />}
    </section>
  );
}

export default CalendarConnectionsSection;
